import React from 'react';
import {
  Card,
  Descriptions,
  Button,
  Space,
  Typography,
  Spin,
  Tag,
  Table,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ArrowLeftOutlined, EditOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import api from '../config/api';

const { Title } = Typography;

// Service type mapping for display
const SERVICE_TYPE_LABELS: { [key: string]: string } = {
  'retailzpos': 'RetailZPOS',
  'ptechpos': 'PTechPOS',
  'moolah_points': 'Moolah Points',
  'patelhms': 'Patel HMS',
  'credit_card_processing': 'Credit Card Processing',
};

const BusinessDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch business
  const { data: business, isLoading } = useQuery(
    ['business', id],
    async () => {
      const response = await api.get(`/api/businesses/${id}`);
      return response.data;
    }
  );

  // Fetch business services
  const { data: businessServices = [] } = useQuery(
    ['business-services', id],
    async () => {
      const response = await api.get(`/api/businesses/${id}/services`);
      return response.data?.services || [];
    }
  );

  // Fetch sales agents for this business
  const { data: salesAgents = [] } = useQuery(
    ['business-sales-agents', id],
    async () => {
      const response = await api.get(`/api/businesses/${id}/sales-agents`);
      return response.data?.salesAgents || [];
    }
  );

  // Fetch business equipment
  const { data: businessEquipment = [] } = useQuery(
    ['business-equipment', id],
    async () => {
      const response = await api.get(`/api/businesses/${id}/equipment`);
      return response.data?.equipment || [];
    }
  );

  // Fetch tickets for this business
  const { data: ticketsData } = useQuery(
    ['business-tickets', id],
    async () => {
      const response = await api.get('/api/tickets', {
        params: { business_id: id, limit: 10 }
      });
      return response.data;
    }
  );

  const tickets = ticketsData?.tickets || [];

  const equipmentColumns: ColumnsType<any> = [
    {
      title: 'Type',
      dataIndex: 'equipment_type',
      key: 'equipment_type',
      render: (type: string) => (
        <Tag color="blue">{type}</Tag>
      ),
    },
    {
      title: 'Model',
      dataIndex: 'model',
      key: 'model',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const getStatusDisplay = (status: string) => {
          switch (status) {
            case 'active':
              return 'Active';
            case 'not_supported':
              return 'Not Supported';
            default:
              return status;
          }
        };
        
        return (
          <Tag color={status === 'active' ? 'green' : 'red'}>
            {getStatusDisplay(status)}
          </Tag>
        );
      },
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
  ];

  const ticketColumns: ColumnsType<any> = [
    {
      title: 'Ticket #',
      dataIndex: 'ticket_number',
      key: 'ticket_number',
      render: (text, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/workspace/number/${record.ticket_number}`)}
          style={{ padding: 0 }}
        >
          #{text}
        </Button>
      ),
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colors: any = { open: 'blue', pending: 'orange', solved: 'green' };
        return <Tag color={colors[status]}>{status.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority) => {
        const colors: any = { low: 'green', medium: 'orange', high: 'red', urgent: 'red' };
        return <Tag color={colors[priority]}>{priority.toUpperCase()}</Tag>;
      },
    },
  ];

  if (isLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!business) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>Business not found</Title>
        <Button onClick={() => navigate('/businesses')}>Back to Businesses</Button>
      </div>
    );
  }

  // Get service labels for display
  const serviceLabels = businessServices.map((service: any) => 
    SERVICE_TYPE_LABELS[service.service_type] || service.service_type
  );

  return (
    <div style={{ padding: 24, background: '#f0f2f5', minHeight: 'calc(100vh - 64px)' }}>
      <Space style={{ marginBottom: 16 }}>
            <Button 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/businesses')}
        >
          Back to Businesses
            </Button>
      </Space>

      <Card
        title={<Title level={2} style={{ margin: 0 }}>{business.business_name}</Title>}
        extra={
                    <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/businesses/${id}/edit`)}
          >
            Edit
                    </Button>
        }
      >
        <Descriptions bordered column={{ xs: 1, sm: 1, md: 2 }}>
          <Descriptions.Item label="MID">{business.mid || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Store ID">{business.store_id || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Contact Person">{business.contact_person || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Phone">{business.phone || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Email">{business.email || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Address" span={2}>
            {[business.address, business.city, business.state, business.zip_code]
              .filter(Boolean)
              .join(', ') || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Country">{business.country || 'N/A'}</Descriptions.Item>
          <Descriptions.Item label="Business Type">
            {business.business_type_name || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Company">
            {business.company_name || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Sales Agents">
            {salesAgents.length > 0 ? (
              <Space wrap>
                {salesAgents.map((agent: any, idx: number) => (
                  <Tag key={idx} color="blue">
                    {agent.first_name} {agent.last_name}
                  </Tag>
                ))}
              </Space>
            ) : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Auto-add ticket followers">
            {business.default_followers && ((business.default_followers.users && business.default_followers.users.length > 0) || (business.default_followers.emails && business.default_followers.emails.length > 0)) ? (
              <Space direction="vertical" size={0}>
                {business.default_followers.users && business.default_followers.users.length > 0 && (
                  <span>
                    <strong>Internal User:</strong> {(business.default_followers.users as any[]).map((u: any) => `${u.first_name} ${u.last_name}`).join(', ')}
                  </span>
                )}
                {business.default_followers.emails && business.default_followers.emails.length > 0 && (
                  <span>
                    <strong>Email only:</strong> {(business.default_followers.emails as string[]).join(', ')}
                  </span>
                )}
              </Space>
            ) : 'None'}
          </Descriptions.Item>
          <Descriptions.Item label="Services" span={2}>
            {serviceLabels.length > 0 ? (
              <Space wrap>
                {serviceLabels.map((label: string, idx: number) => (
                  <Tag key={idx} color="blue">{label}</Tag>
                ))}
              </Space>
            ) : 'N/A'}
          </Descriptions.Item>
          {business.retailzpos_portal && (
            <Descriptions.Item label="Portal" span={2}>
              {business.retailzpos_portal}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Email Notifications" span={2}>
            <Tag color={business.exclude_from_notifications ? 'red' : 'green'}>
              {business.exclude_from_notifications ? 'Excluded' : 'Enabled'}
            </Tag>
            {business.exclude_from_notifications && (
              <span style={{ marginLeft: 8, fontSize: '12px', color: '#666' }}>
                This business's tickets are excluded from email notifications and feedback
              </span>
            )}
          </Descriptions.Item>
        </Descriptions>
          </Card>

      <Card title="Assigned Equipment" style={{ marginTop: 24 }}>
        <Table
          columns={equipmentColumns}
          dataSource={businessEquipment}
          rowKey="id"
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: 'No equipment assigned' }}
        />
      </Card>

      <Card title="Recent Tickets" style={{ marginTop: 24 }}>
        <Table
          columns={ticketColumns}
          dataSource={tickets}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
          </Card>
    </div>
  );
};

export default BusinessDetail;
